import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { PreviewAutomationStepNewsletterModal } from '../../../../components/automation/PreviewAutomationStepNewsletterModal';
import { HtmlNewsletterStepForm } from '../../components/HtmlNewsletterStepForm';
import { HtmlNewsletterStepAdvancedOptions } from '../../components/HtmlNewsletterStepAdvancedOptions';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useHtmlNewsletterStepState } from '../../hooks/useHtmlNewsletterStepState';
import { useHtmlNewsletterStepSave } from '../../hooks/useHtmlNewsletterStepSave';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { rendererContainer } from './styles/commonRendererStyles';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';

interface HtmlNewsletterStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function HtmlNewsletterStepRenderer({ nodeId, data, readOnly: readOnlyProp }: HtmlNewsletterStepRendererProps) {
	const {
		step: newsletterStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
	} = useStepRenderer<Api.IHtmlNewsletterAutomationStep>(data, readOnlyProp);

	const { updateNodeData } = useAutomationFlowContext();
	const stepType = data.stepType;
	const schedule = newsletterStep?.schedule || stepData?.schedule;

	const [showEmailEditor, setShowEmailEditor] = React.useState(false);

	const {
		name,
		setName,
		tagToAddOnClick,
		setTagToAddOnClick,
		showEmailLinkClickTags,
		setShowEmailLinkClickTags,
		skipStepContactFilter,
		setSkipStepContactFilter,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		showRequiredFieldError,
		autoSave,
		clearInlineErrorMessage,
	} = useHtmlNewsletterStepState({
		newsletterStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const { save } = useHtmlNewsletterStepSave({
		newsletterStep,
		stepId: stepId || '',
		templateId: template?.id,
		name,
		tagToAddOnClick,
		skipStepContactFilter,
		autoSave,
	});

	const syncStepToNodeData = (updatedFields: Partial<Api.IHtmlNewsletterAutomationStep>) => {
		if (!updateNodeData || !newsletterStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...newsletterStep, ...updatedFields },
		});
	};

	const handleNameChange = (newName: string) => {
		setName(newName);
		clearInlineErrorMessage();
		// Keep node header/compact title in sync while typing.
		syncStepToNodeData({ name: newName });
	};

	const handleNameBlur = () => {
		save();
		syncStepToNodeData({ name });
	};

	const handleSelectTemplate = () => {
		setShowEmailEditor(true);
		clearInlineErrorMessage();
	};

	const handleTagToAddOnClickChange = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
		syncStepToNodeData({ tagToAddOnClick: tag });
	};

	const handleShowEmailLinkClickTagsChange = (show: boolean) => {
		setShowEmailLinkClickTags(show);
		if (!show) {
			handleTagToAddOnClickChange('');
		}
	};

	const handleContactFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const handleSendFromChange = (sendFrom: Api.ISendFromOptions, sendToSender: boolean) => {
		save({ newSendFrom: sendFrom, newSendToSender: sendToSender });
		syncStepToNodeData({
			sendFromOptions: sendFrom,
			options: {
				...newsletterStep?.options,
				sendToSender,
			},
		});
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const handleEmailComposerModalRequestClose = (result?: Api.IHtmlNewsletterAutomationStep | null) => {
		if (result) {
			syncStepToNodeData(result);
		}
		setShowEmailEditor(false);
	};

	const hasContent = !!newsletterStep?.content || !!stepData?.hasContent;
	const baseKey = `${stepType}-${nodeId}-${newsletterStep?.id || ''}`;

	return (
		<div className={css(rendererContainer)}>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{newsletterStep && trigger && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={newsletterStep}
					index={0}
					onScheduleChange={handleScheduleChange}
				/>
			)}
			<HtmlNewsletterStepForm
				name={name}
				hasContent={hasContent}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				baseKey={baseKey}
				errorMessage={showRequiredFieldError ? 'Name is required' : null}
				onNameChange={handleNameChange}
				onNameBlur={handleNameBlur}
				onSelectTemplate={handleSelectTemplate}
				onClearError={clearInlineErrorMessage}
			/>
			<HtmlNewsletterStepAdvancedOptions
				step={newsletterStep || null}
				tagToAddOnClick={tagToAddOnClick}
				showEmailLinkClickTags={showEmailLinkClickTags}
				skipStepContactFilter={skipStepContactFilter}
				advancedOptionsToggled={advancedOptionsToggled}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				baseKey={baseKey}
				onToggleAdvancedOptions={setAdvancedOptionsToggled}
				onShowEmailLinkClickTagsChange={handleShowEmailLinkClickTagsChange}
				onTagToAddOnClickChange={handleTagToAddOnClickChange}
				onContactFilterChange={handleContactFilterChange}
				onSendFromChange={handleSendFromChange}
				impersonationContext={undefined}
				connectionTypes={undefined}
			/>
			{showEmailEditor && template && (
				<PreviewAutomationStepNewsletterModal
					automationTemplateId={template.id}
					step={newsletterStep}
					impersonationContext={undefined}
					onRequestClose={handleEmailComposerModalRequestClose}
					onStepChanged={() => {
						// No-op: step changes are tracked via auto-save
					}}
					readonly={readonly}
				/>
			)}
		</div>
	);
}
